// src/pages/checklists/ChecklistsAbertosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import FiltroBar from "../../components/FiltroBar/FiltroBar";
import { db } from "../../config/firebase";

function tsToDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function formatDateBR(value) {
  const dt = tsToDate(value);
  if (!dt) return "-";
  return dt.toLocaleDateString("pt-BR");
}

export default function ChecklistsAbertosPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [items, setItems] = useState([]);
  const [deletandoId, setDeletandoId] = useState(null);
  const [campoFiltro, setCampoFiltro] = useState("checklist");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const camposFiltro = [
    { value: "checklist", label: "Checklist" },
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Equipamento" },
    { value: "setorNome", label: "Academia" },
  ];

  async function handleExcluir(maquina) {
    if (!maquina?.id) return;

    const confirmar = window.confirm(
      "Remover este checklist da máquina? Esta ação não exclui o equipamento."
    );
    if (!confirmar) return;

    setErro("");
    setDeletandoId(maquina.id);
    try {
      await updateDoc(doc(db, "maquinas", maquina.id), {
        checklistInstalacaoAtiva: false,
        checklistInstalacaoId: deleteField(),
        checklistInstalacaoNome: deleteField(),
        checklistConcluido: deleteField(),
        checklistConcluidoEm: deleteField(),
        checklistConcluidoAtendimentoId: deleteField(),
        checklistUltimaExecucao: deleteField(),
        atualizadoEm: serverTimestamp(),
      });

      setItems((prev) => prev.filter((m) => m.id !== maquina.id));
    } catch (e) {
      console.error("Erro ao remover checklist aberto:", e);
      setErro("Falha ao remover checklist. Tente novamente.");
    } finally {
      setDeletandoId(null);
    }
  }

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      if (!usuario?.grupoId) {
        setItems([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setErro("");
      try {
        const q = query(
          collection(db, "maquinas"),
          where("grupoId", "==", usuario.grupoId)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((maquina) => {
            return (
              maquina?.checklistInstalacaoAtiva === true &&
              !!maquina?.checklistInstalacaoId &&
              !maquina?.checklistConcluido
            );
          })
          .sort((a, b) => {
            const da = tsToDate(a.criadoEm || a.createdAt)?.getTime() || 0;
            const dbv = tsToDate(b.criadoEm || b.createdAt)?.getTime() || 0;
            return dbv - da;
          });
        if (!cancelado) setItems(lista);
      } catch (e) {
        console.error(e);
        if (!cancelado) setErro("Falha ao carregar checklists abertos.");
      } finally {
        if (!cancelado) setLoading(false);
      }
    }

    carregar();
    return () => {
      cancelado = true;
    };
  }, [usuario?.grupoId]);

  const itemsFiltrados = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return items;

    return items.filter((maquina) => {
      const valor =
        campoFiltro === "checklist"
          ? String(maquina.checklistInstalacaoNome || "Checklist")
          : String(maquina[campoFiltro] || "");
      return valor.toLowerCase().includes(texto);
    });
  }, [items, campoFiltro, textoFiltro]);

  const conteudoMobile = useMemo(() => {
    if (loading) {
      return <section className="pc-card">Carregando checklists abertos...</section>;
    }

    if (erro) {
      return <section className="pc-card">{erro}</section>;
    }

    if (items.length === 0) {
      return (
        <section className="pc-card">
          <h2 style={{ marginTop: 0 }}>Checklists abertos</h2>
          <p style={{ marginTop: 8, lineHeight: 1.5 }}>
            Nenhum checklist de instalação pendente no momento.
          </p>
        </section>
      );
    }

    return (
      <section className="pc-card">
        <h2 style={{ marginTop: 0 }}>Checklists abertos</h2>
        <p style={{ marginTop: 8, lineHeight: 1.5 }}>
          Checklists de instalação aguardando execução (fora de OS).
        </p>

        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          {itemsFiltrados.map((maquina) => (
            <div
              key={maquina.id}
              style={{
                border: "1px solid var(--cor-borda, rgba(148,163,184,.3))",
                borderRadius: 12,
                padding: 12,
                background: "var(--cor-card-alt, var(--cor-card))",
              }}
            >
              <div style={{ fontWeight: 700 }}>
                {maquina.codigo || maquina.id} • {maquina.checklistInstalacaoNome || "Checklist"}
              </div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                Equipamento: {maquina.nome || "-"} • Academia: {maquina.setorNome || "-"} • Cadastro: {formatDateBR(maquina.criadoEm || maquina.createdAt)}
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="pc-btn-primario"
                  onClick={() => navigate(`/checklists/executar/${maquina.id}`)}
                >
                  Abrir execução
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }, [erro, itemsFiltrados, loading, navigate]);

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Checklists abertos"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((v) => !v)}
        abaBottom="home"
      >
        {conteudoMobile}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Checklists Abertos"
      subtitulo="Checklist / Abertos"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Checklists abertos</h3>
            <p>Lista de checklists pendentes para execução.</p>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar checklists abertos..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {loading ? (
            <p style={{ padding: 10 }}>Carregando checklists abertos...</p>
          ) : itemsFiltrados.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum checklist pendente no momento.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th>Código</th>
                  <th>Checklist</th>
                  <th>Equipamento</th>
                  <th>Academia</th>
                  <th>Cadastro</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {itemsFiltrados.map((maquina) => {
                  const aberto = expandedId === maquina.id;
                  return (
                    <React.Fragment key={maquina.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() => setExpandedId((prev) => (prev === maquina.id ? null : maquina.id))}
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>▸</span>
                          </button>
                        </td>
                        <td>{maquina.codigo || "-"}</td>
                        <td>{maquina.checklistInstalacaoNome || "Checklist"}</td>
                        <td>{maquina.nome || "-"}</td>
                        <td>{maquina.setorNome || "-"}</td>
                        <td>{formatDateBR(maquina.criadoEm || maquina.createdAt)}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <button
                            type="button"
                            className="pc-btn-primario"
                            style={{ fontSize: 13, padding: "4px 10px" }}
                            onClick={() => navigate(`/checklists/executar/${maquina.id}`)}
                          >
                            Abrir execução
                          </button>
                          <button
                            type="button"
                            className="pc-icon-btn"
                            title="Remover checklist"
                            style={{ marginLeft: 8 }}
                            disabled={deletandoId === maquina.id}
                            onClick={() => handleExcluir(maquina)}
                          >
                            {deletandoId === maquina.id ? "..." : "🗑️"}
                          </button>
                        </td>
                      </tr>

                      {aberto ? (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Checklist ID</div>
                                  <div className="pc-expand-value">{maquina.checklistInstalacaoId || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Grupo</div>
                                  <div className="pc-expand-value">{maquina.grupoId || "-"}</div>
                                </div>
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Status</div>
                                  <div className="pc-expand-value">Pendente</div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}
